/**
 * buildFreshness — an open tab learns that a newer build was deployed (WEB-APP-VERSION-REFRESH-001).
 *
 * Every build stamps its identity into its HTML (`<meta name="app-build-id">`) and into `version.json`
 * (`@puffin-core/shared-config`'s build identity plugin). The tab's own build is the stamp in the document it was
 * loaded with — exactly what a reload refreshes — and the deployment's is `version.json`, served `no-cache`. When the
 * two differ, the tab is STALE: a newer build is ready, and the shell offers a reload at a safe moment.
 *
 * A server that refuses this client as too old (426 `CLIENT_TOO_OLD`, judged on the `X-Client-Contract` every request
 * carries) makes the reload REQUIRED at once, with no poll: the facade reports it here. REQUIRED is never downgraded.
 *
 * Polling follows the page's attention: on a cadence only while the page is visible, and once the moment it becomes
 * visible again. A page with no build stamp (a dev server) has nothing to compare and never polls.
 *
 * The monitor knows NOTHING about when a reload is safe — that is page-shell's reload-safety gate, which reads this
 * state. Nothing here reloads.
 *
 * @module @puffin-core/api-client/buildFreshness
 */

import { BUILD_IDENTITY } from '@puffin-core/client-contract';

import { readDocumentBuild } from './buildIdentity.js';
import { createFetchSeam } from './createFetchSeam.js';

/** How often a visible page asks whether a newer build was deployed. */
export const BUILD_FRESHNESS_POLL_MS = 5 * 60 * 1000;

/** What a tab knows about its build. */
export const BUILD_FRESHNESS_STATUS = Object.freeze({
  /** The build this tab runs is the deployed one, as far as it knows. */
  CURRENT: 'current',
  /** A newer build was deployed; this tab may keep working until a safe moment. */
  STALE: 'stale',
  /** A server refused this build as too old; reload at the next safe moment, without waiting. */
  REQUIRED: 'required',
});

const fetchVersionFile = createFetchSeam({ label: 'fetchDeployedBuild' });

/**
 * Read the deployment's build identity — fresh, never from a cache.
 *
 * @param {Object} [options]
 * @param {Function} [options.fetch] - a fetch-shaped function (the page's own by default)
 * @returns {Promise<{buildId: string, builtAt: string}>}
 * @throws {Error} when the answer is not OK
 */
export async function fetchDeployedBuild({ fetch = fetchVersionFile } = {}) {
  const response = await fetch(`/${BUILD_IDENTITY.VERSION_FILE}`, {
    cache: 'no-store',
    credentials: 'same-origin',
  });
  if (!response.ok) {
    throw new Error(`${BUILD_IDENTITY.VERSION_FILE} answered ${response.status}`);
  }
  return response.json();
}

/**
 * @param {Object} options
 * @param {{id: string}|null} options.ownBuild - the build this page was loaded with; `null` on a dev server
 * @param {() => Promise<{buildId?: string}>} [options.fetchLatest=fetchDeployedBuild] - the deployment's identity
 * @param {Document|undefined} [options.doc] - the page, for its visibility (`visibilitychange`)
 * @param {() => number} [options.now=Date.now] - the clock (ms)
 * @param {number} [options.intervalMs=BUILD_FRESHNESS_POLL_MS] - the poll cadence while visible
 * @returns {Readonly<{
 *   start: () => boolean, stop: () => void, check: () => Promise<void>, requireReload: () => void,
 *   getState: () => Readonly<Object>, subscribe: (listener: Function) => () => void,
 * }>}
 */
export function createBuildFreshnessMonitor({
  ownBuild,
  fetchLatest = fetchDeployedBuild,
  doc,
  now = Date.now,
  intervalMs = BUILD_FRESHNESS_POLL_MS,
}) {
  let state = Object.freeze({
    status: BUILD_FRESHNESS_STATUS.CURRENT,
    ownBuildId: ownBuild ? ownBuild.id : null,
    latest: null,
    detectedAt: null,
    lastPollError: null,
  });
  const listeners = new Set();
  let timer = null;

  const update = (changes, { news }) => {
    state = Object.freeze({ ...state, ...changes });
    if (news) for (const listener of listeners) listener(state);
  };

  const isVisible = () => !doc || doc.visibilityState !== 'hidden';

  const check = async () => {
    if (!ownBuild) return;
    let latest;
    try {
      latest = await fetchLatest();
    } catch (error) {
      // A failed poll (offline, a deploy mid-flight) is this poll's OUTCOME, recorded on the state; it is not news,
      // and the next poll asks again.
      update({ lastPollError: error.message }, { news: false });
      return;
    }
    if (!latest || typeof latest.buildId !== 'string') {
      update(
        { lastPollError: `${BUILD_IDENTITY.VERSION_FILE} names no buildId` },
        { news: false },
      );
      return;
    }
    const isNewer = latest.buildId !== ownBuild.id;
    const becomesStale = isNewer && state.status === BUILD_FRESHNESS_STATUS.CURRENT;
    update(
      {
        lastPollError: null,
        latest: isNewer ? latest : state.latest,
        status: becomesStale ? BUILD_FRESHNESS_STATUS.STALE : state.status,
        detectedAt: becomesStale ? now() : state.detectedAt,
      },
      { news: becomesStale },
    );
  };

  const onVisibilityChange = () => {
    if (isVisible()) check();
  };

  return Object.freeze({
    /**
     * Begin polling (only while visible) and checking on return to the page. Idempotent.
     *
     * @returns {boolean} whether the monitor is polling — never on a page with no build stamp
     */
    start() {
      if (!ownBuild) return false;
      if (timer === null) {
        timer = setInterval(() => {
          if (isVisible()) check();
        }, intervalMs);
        doc?.addEventListener('visibilitychange', onVisibilityChange);
      }
      return true;
    },

    /** Stop polling and listening. Idempotent. */
    stop() {
      if (timer !== null) {
        clearInterval(timer);
        timer = null;
        doc?.removeEventListener('visibilitychange', onVisibilityChange);
      }
    },

    check,

    /**
     * The reload is required, at the next safe moment: a server refused this build as too old (426 CLIENT_TOO_OLD,
     * via the facade), or this build's own files are gone (a route chunk 404, via error-handling's chunk reloader).
     */
    requireReload() {
      if (state.status === BUILD_FRESHNESS_STATUS.REQUIRED) return;
      update(
        { status: BUILD_FRESHNESS_STATUS.REQUIRED, detectedAt: state.detectedAt ?? now() },
        { news: true },
      );
    },

    /** @returns {Readonly<Object>} what this tab knows about its build */
    getState() {
      return state;
    },

    /**
     * @param {(state: Readonly<Object>) => void} listener - told each change of status
     * @returns {() => void} unsubscribe
     * @throws {TypeError} when `listener` is not a function
     */
    subscribe(listener) {
      if (typeof listener !== 'function') {
        throw new TypeError('buildFreshness.subscribe: `listener` must be a function.');
      }
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  });
}

let pageMonitor = null;

/**
 * The page's monitor — one per document. It does not start itself: the shell starts it when it mounts the notice,
 * so a test or a script that merely imports a client never begins polling.
 *
 * @returns {ReturnType<typeof createBuildFreshnessMonitor>}
 */
export function getPageBuildFreshnessMonitor() {
  if (!pageMonitor) {
    // `undefined` where there is no DOM: then there is no stamp to compare and no visibility to follow.
    const doc = globalThis.document;
    pageMonitor = createBuildFreshnessMonitor({ ownBuild: readDocumentBuild(doc), doc });
  }
  return pageMonitor;
}
